import React, { useEffect, useState } from "react";
import { Button, Empty, Progress, Spin } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getPmHome } from "../../../../services/features/dashboard.service";
import {
  stageBandLabel,
  getCompletenessColor,
} from "../../../../services/helpFunctions/ipCompleteness";
import { RolesEnum } from "../../../../services/rolesEnum";
import { isAuthorized } from "../../../../services/authService";
import NudgeModal from "../../../components/NudgeModal/NudgeModal";
import { HOME_COLORS, muted } from "../homeTheme";
import FlagPatternsPanel from "../../../components/FlagPatterns/FlagPatternsPanel";

// F-01 — PM home. One call (/api/Dashboard/pmHome) returns the whole screen:
//   projectHealth  — the TPM summary rollups + stalledOverview + flagPatterns
//   ipCompleteness — every IP plan's score, laggards first
// so the PM gets project health in one place instead of several disconnected
// panels. Nothing here is editable: it's a read-only rollup that links out.
const PmHomePanel = () => {
  const navigate = useNavigate();
  const projectID = localStorage.getItem("currentProjectID");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [nudgeTarget, setNudgeTarget] = useState(null);

  const canNudge = isAuthorized([
    RolesEnum.ProjectManager,
    RolesEnum.SystemAdmin,
  ]);

  useEffect(() => {
    if (!projectID) {
      setLoading(false);
      return;
    }
    getPmHome(
      projectID,
      (res) => {
        setData(res?.data || res || null);
        setLoading(false);
      },
      () => {
        setFailed(true);
        setLoading(false);
      }
    );
  }, [projectID]);

  if (loading) {
    return (
      <div className="homePanel">
        <div className="homePanelLoading">
          <Spin />
        </div>
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="homePanel">
        <div className="homePanelHead">
          <h3>Project health</h3>
        </div>
        <Empty description="Couldn't load the project health rollup" />
      </div>
    );
  }

  const health = data.projectHealth || {};
  const location = health.activityByLocation || {};
  const stalled = health.stalledOverview || {};
  const stalledVisits = stalled.stalledSiteVisits || 0;
  const stalledTasks =
    stalled.stalledTasks ?? health.tasksOverviewResponse?.summary?.stalled ?? 0;

  const planned = location.plannedVisits || 0;
  const completed = location.completedVisits || 0;
  const coverage = planned > 0 ? Math.round((completed / planned) * 100) : null;

  const KPIS = [
    {
      key: "coverage",
      value: coverage == null ? "—" : `${coverage}%`,
      label: `Visit coverage (${completed}/${planned})`,
      color: HOME_COLORS.brand,
      path: "/project/planning?visits",
    },
    {
      key: "flags",
      value: health.activitySnapshot?.activeRedFlagCards ?? 0,
      label: "Active red flags",
      color: HOME_COLORS.alert,
      path: "/project/flags?Flags",
    },
    {
      key: "stalledVisits",
      value: stalledVisits,
      label: "Stalled site visits",
      color: HOME_COLORS.stalled,
      path: "/project/planning?visits",
    },
    {
      key: "stalledTasks",
      value: stalledTasks,
      label: "Stalled tasks",
      color: HOME_COLORS.stalled,
      path: "/project/planning?tasks",
    },
  ];

  // Laggards first — the endpoint already sorts this way; the panel shows the
  // worst handful so the PM can act, with the board a click away.
  const ipList = Array.isArray(data.ipCompleteness) ? data.ipCompleteness : [];

  return (
    <>
      <div className="homePanel">
        <div className="homePanelHead">
          <h3>Project health</h3>
          <span className="homePanelCount">
            Full rollups on the TPM Summary tab
          </span>
        </div>

        <div className="homeHubKpis">
          {KPIS.map((k) => (
            <div
              key={k.key}
              className="homeHubKpi"
              onClick={() => navigate(k.path)}
              role="button"
            >
              <span className="homeHubKpiValue">{k.value}</span>
              <span className="homeHubKpiLabel">
                <span className="homeDot" style={{ background: k.color }} />
                {k.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="homeRow2">
        {/* Active flag concentrations, most-concentrated first. */}
        <div className="homePanel">
          <FlagPatternsPanel patterns={health.flagPatterns} limit={5} />
        </div>

        <div className="homePanel">
          <div className="homePanelHead">
            <h3>IP completeness — needs attention</h3>
            <span
              className="homePanelLink"
              onClick={() => navigate("/project/planning?ip-planning")}
            >
              IP Planning Board
            </span>
          </div>
          {ipList.length === 0 ? (
            <Empty description="No IP plans yet" />
          ) : (
            <div className="homeListRows">
              {ipList.slice(0, 6).map((ip) => {
                const percent = Math.round(ip.percentage || 0);
                const name =
                  ip.implementingPartnerName || ip.iPPlanCode || "IP plan";
                return (
                  <div className="homeIpRow" key={ip.iPPlanId}>
                    <div className="homeIpRowHead">
                      <span
                        className="homeListRowTitle"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/project/planning?ip-planning")}
                      >
                        {name}
                      </span>
                      <span
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                      >
                        <span className="homeIpBand">
                          {stageBandLabel(ip.stageBand)}
                        </span>
                        {canNudge && (
                          <Button
                            type="text"
                            size="small"
                            icon={<BellOutlined />}
                            title="Nudge assignee"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNudgeTarget({ id: ip.iPPlanId, name });
                            }}
                          />
                        )}
                      </span>
                    </div>
                    <Progress
                      percent={percent}
                      size="small"
                      strokeColor={muted(getCompletenessColor(percent))}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <NudgeModal
        open={!!nudgeTarget}
        ipPlanId={nudgeTarget?.id}
        ipName={nudgeTarget?.name}
        onClose={() => setNudgeTarget(null)}
      />
    </>
  );
};

export default PmHomePanel;
